import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowRight, QrCode } from 'lucide-react';

import { resolveScan } from '@/lib/scanRouting';
import QrScanner from '@/components/phone/QrScanner';
import { Colors } from '@/components/phone/theme';

/**
 * Scan a job card sticker, or type its number.
 *
 * Both halves matter. The camera is the ordinary way in; the box under it is
 * what a torn sticker leaves, and it is also the only way in on a browser that
 * will not open the camera — a page served over plain HTTP, or permission
 * refused once and not asked again.
 *
 * Mirrors `mobile-frontend/app/(tabs)/scan.tsx`.
 */
export default function SupervisorScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [manualId, setManualId] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [blocked, setBlocked] = useState(null);

  const handleScan = async (data) => {
    if (!String(data || '').trim()) return;
    setCameraOpen(false);
    setBlocked(null);
    const outcome = await resolveScan(data, navigate, t);
    if (!outcome.ok) setBlocked(outcome);
  };

  return (
    <div className="flex flex-1 flex-col px-5">
      <div className="pb-4 pt-6">
        <p className="text-[11px] font-medium tracking-[1.5px]" style={{ color: Colors.accent.DEFAULT }}>
          QR SCANNER
        </p>
        <h1 className="text-[22px] font-bold -tracking-[0.5px]" style={{ color: Colors.text.primary }}>
          {t('scan.title')}
        </h1>
      </div>

      {blocked && (
        <div
          data-testid="scan-blocked"
          className="mt-2 flex flex-col items-center gap-2 rounded-[18px] border p-5 text-center"
          style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4' }}
        >
          <AlertCircle className="h-9 w-9" style={{ color: Colors.semantic.caution }} />
          <p className="text-[17px] font-bold" style={{ color: Colors.semantic.caution }}>{blocked.title}</p>
          <p className="text-[15px] font-medium leading-6" style={{ color: Colors.semantic.caution }}>
            {blocked.message}
          </p>
          <button
            type="button"
            data-testid="scan-again-btn"
            onClick={() => setBlocked(null)}
            className="mt-2 rounded-[14px] border px-6 py-3 text-[13px] font-bold"
            style={{
              backgroundColor: Colors.background.primary,
              borderColor: '#E8DCC4',
              color: Colors.semantic.caution,
            }}
          >
            {t('scan.scan_again')}
          </button>
        </div>
      )}

      <div className="flex flex-col items-center py-8">
        <button
          type="button"
          data-testid="open-camera-btn"
          onClick={() => { setBlocked(null); setCameraOpen(true); }}
          className="flex h-[180px] w-[180px] flex-col items-center justify-center gap-3 rounded-full shadow-lg"
          style={{
            backgroundImage: `linear-gradient(135deg, ${Colors.accent.DEFAULT}, ${Colors.accent.light})`,
          }}
        >
          <QrCode className="h-[52px] w-[52px]" style={{ color: Colors.text.inverse }} />
          <span className="text-[15px] font-bold" style={{ color: Colors.text.inverse }}>{t('scan.title')}</span>
        </button>
        <p className="mt-6 max-w-[16rem] text-center text-[13px] font-medium leading-6" style={{ color: Colors.text.secondary }}>
          {t('scan.instruction')}
        </p>
      </div>

      <div className="my-6 flex items-center">
        <span className="h-px flex-1" style={{ backgroundColor: Colors.border.default }} />
        <span className="mx-4 text-[11px] tracking-[1px]" style={{ color: Colors.text.disabled }}>
          {t('scan.manual_entry')}
        </span>
        <span className="h-px flex-1" style={{ backgroundColor: Colors.border.default }} />
      </div>

      <form
        className="flex gap-3"
        onSubmit={(e) => { e.preventDefault(); handleScan(manualId); }}
      >
        <input
          data-testid="manual-id-input"
          className="h-14 min-w-0 flex-1 rounded-[18px] border px-5 text-[15px] font-medium uppercase outline-none"
          style={{
            backgroundColor: Colors.background.primary,
            borderColor: Colors.border.default,
            color: Colors.text.primary,
          }}
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          placeholder={t('scan.enter_id')}
          aria-label={t('scan.enter_id')}
          autoCapitalize="characters"
        />
        <button
          type="submit"
          data-testid="manual-lookup-btn"
          disabled={!manualId.trim()}
          aria-label={t('scan.scan_btn')}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] disabled:opacity-40"
          style={{
            backgroundImage: `linear-gradient(90deg, ${Colors.accent.DEFAULT}, ${Colors.accent.light})`,
          }}
        >
          <ArrowRight className="h-[22px] w-[22px]" style={{ color: Colors.text.inverse }} />
        </button>
      </form>

      <QrScanner open={cameraOpen} onScan={handleScan} onClose={() => setCameraOpen(false)} />
    </div>
  );
}
